import React, { lazy, Suspense, useEffect } from 'react'
import {
  Route,
  Redirect,
  BrowserRouter as Router,
  Switch,
  useLocation,
  useParams,
} from 'react-router-dom'
import { QueryParamProvider } from 'use-query-params'
import NiceModal from '@ebay/nice-modal-react'

import { Spin, GTMPageView, useAuthState, useShowMessage } from '@pepper/ui'
import { checkAuthorization } from '@pepper/utils/src/api/functions/authHelpers'
import Dashboard from 'container/Dashboard/Dashboard'
import PublicDocs from 'container/PublicDocument/PublicDocument'
import ErrorBoundary from './ErrorBoundary'

const SignUp = lazy(() => import('container/Signup/Signup'))
const Login = lazy(() => import('container/Login/Login'))
const ForgotPassword = lazy(
  () => import('container/ForgotPassword/ForgotPassword')
)
const Invites = lazy(() => import('container/Invites/Invites'))
const CreateOrganisation = lazy(
  () => import('container/CreateOrganisation/CreateOrganisation')
)
const Document = lazy(() => import('container/Document/Document'))
const Template = lazy(() => import('container/Templates/Templates'))
const PrivateDocument = lazy(
  () => import('container/PrivateDocument/PrivateDocument')
)
const PepperDocs = lazy(() => import('container/PepperDocs/PepperDocs'))
const PrivateItemPublicDocs = lazy(
  () => import('container/PublicDocument/PrivateDocumentPublic')
)
const CreateProjectFlow = lazy(
  () => import('container/Projects/CreateProjectFlow/CreateProjectFlow')
)

const GoogleRedirect = lazy(
  () =>
    import(
      'container/Profile/TabContent/Domains/GoogleIntegration/GoogleRedirect'
    )
)
const OnboardingUser = lazy(
  () => import('container/OnboardingUser/OnboardingUser')
)
const Payment = lazy(() => import('container/Payment/Payment'))
const PublicGapReport = lazy(
  () => import('container/SEOTools/PublicGapReport/PublicGapReport')
)

const publicRoutes = [
  {
    path: '/login',
    exact: true,
    component: <Login />,
  },
  {
    path: '/signup',
    exact: true,
    component: <SignUp />,
  },
  {
    path: '/forgot-password',
    exact: true,
    component: <ForgotPassword />,
  },
  {
    path: '/:companyId/payments',
    exact: true,
    component: <Payment />,
  },
  {
    path: '/unsubscribe-email',
    exact: false,
    component: lazy(
      () => import('container/UnsubscribeEmail/UnsubscribeEmail')
    ),
  },
  {
    path: '/content-refresh-report/:reportId/:documentId',
    exact: true,
    component: <PublicGapReport />,
  },
]

const privateRoutes = [
  {
    path: '/document/:assignmentId',
    component: <Document />,
  },
  {
    path: '/items/:itemId',
    component: <PrivateDocument />,
  },
  {
    path: '/template/:templateId',
    component: <Template />,
  },
  {
    path: '/docs/:pepperDocId',
    component: <PepperDocs />,
  },
  {
    path: '/create-project-flow',
    component: <CreateProjectFlow />,
  },
  {
    path: '/',
    component: <Dashboard />,
  },
]

const CreateProjectRedirection = () => {
  const { user } = useAuthState()
  const latestCompanyId = user?.companies[user?.companies.length - 1]

  if (latestCompanyId) {
    window.location.href = `/${latestCompanyId}?ossm=1`
  }

  return (
    <div className="center">
      <Spin />
    </div>
  )
}

const PrivateRoute: React.FC<{
  path: string
}> = ({ children, path }) => {
  const showMessage = useShowMessage()
  const { status, error } = checkAuthorization()
  useEffect(() => {
    if (status === 'error') {
      showMessage({ type: 'error', message: error })
    }
  }, [status, error, showMessage])

  if (status === 'ok') {
    return <Route path={path}>{children}</Route>
  } else if (status === 'error') {
    window.location.href = '/login'
  }
  return null
}

export const RouterContext = React.createContext({ companyId: '' })

const NestedRoutes: React.FC = () => {
  const { companyId } = useParams<{ companyId: string }>()
  const { user } = useAuthState()

  const idxOfSelectedCompany = user.companies.findIndex(
    _company => _company === companyId
  )
  const isValidCompanyId = idxOfSelectedCompany !== -1
  const activeCompany = isValidCompanyId
    ? user.company_details[idxOfSelectedCompany]
    : null

  useEffect(() => {
    if (activeCompany === null) {
      return
    }

    // add company details to dataLayer when active company is changed
    window.dataLayer?.push({
      client_type: activeCompany.type,
      company_id: activeCompany.id,
      company_name: activeCompany.company_name,
    })
    // sent `user-company-change` event on GTM
    window.dataLayer?.push({
      event: 'user-company-change',
    })
  }, [activeCompany])

  // if user doesn't belongs current selected company & not accepted invites
  // then forcefully redirect user to /invites route for joining organisation
  // if (
  //   typeof activeCompany?.invite_accepted !== 'undefined' &&
  //   !activeCompany?.invite_accepted &&
  //   location.pathname !== '/invites'
  // ) {
  //   window.location.href = '/invites'
  // }

  if (user.uid && !isValidCompanyId) {
    return <Redirect to="/" />
  }

  return (
    <RouterContext.Provider value={{ companyId }}>
      <Router basename={companyId}>
        <GTMPageView />
        <QueryParamProvider ReactRouterRoute={Route}>
          <NiceModal.Provider>
            <Switch>
              {privateRoutes.map((route, index) => (
                <PrivateRoute key={index} path={route.path}>
                  {route.component}
                </PrivateRoute>
              ))}
            </Switch>
          </NiceModal.Provider>
        </QueryParamProvider>
      </Router>
    </RouterContext.Provider>
  )
}

const HomeRoute = (): JSX.Element => {
  const location = useLocation()
  const { user } = useAuthState()

  const isAuthorized = checkAuthorization().status
  if (isAuthorized === 'error' || isAuthorized !== 'ok') {
    return <Redirect to={{ pathname: '/login', state: { from: location } }} />
  }

  if (!user.uid)
    return (
      <div className="center">
        <Spin />
      </div>
    )

  const companyId = user?.company_details?.[0]?.id
  const inviteId = user?.company_details?.[0]?.invite_id
  const inviteAccepted = user?.company_details?.[0]?.invite_accepted

  if (!companyId) return <Redirect to="/onboard-user" />
  else if (companyId && !inviteAccepted && inviteId) {
    return (
      <Redirect
        to={`/onboard-user?invite-code=${inviteId}#organisation-details`}
      />
    )
  }

  return <Redirect to={`/${companyId}/home`} />
}

const Routes = (): JSX.Element => {
  // ignore default suspense fallback if user is navigated from typeform
  const ignoreDefaultFallback = [
    '/login',
    '/signup',
    '/forgot-password',
  ].includes(window.location.pathname)
  // Ref: https://stackoverflow.com/a/63875330/4591241
  return (
    <ErrorBoundary>
      <Suspense
        fallback={
          !ignoreDefaultFallback ? <Spin className="spinner-full" /> : null
        }
      >
        <Router>
          {publicRoutes
            .map(route => route.path)
            .includes(window.location.pathname) && <GTMPageView />}

          <QueryParamProvider ReactRouterRoute={Route}>
            <Switch>
              {publicRoutes.map((route, index) => (
                <Route
                  key={index}
                  path={route.path}
                  exact={route.exact}
                  render={() => route.component}
                />
              ))}
              <Route
                key="PUBLIC_VIEW"
                path="/d/:assignmentId"
                exact={false}
                render={() => (
                  <NiceModal.Provider>
                    <PublicDocs />
                  </NiceModal.Provider>
                )}
              />
              <Route
                key="PRIVATE_ITEM_PUBLIC_VIEW"
                path="/items/:itemId"
                exact={false}
                render={() => (
                  <NiceModal.Provider>
                    <PrivateItemPublicDocs />
                  </NiceModal.Provider>
                )}
              />
              <Route
                key="GOOGLE_INTEGRATION_OAUTH_REDIRECT"
                path="/google-redirect"
                render={() => <GoogleRedirect />}
              />
              <PrivateRoute path="/invites">
                <Invites />
              </PrivateRoute>
              <PrivateRoute path="/create-organisation">
                <CreateOrganisation />
              </PrivateRoute>
              <PrivateRoute path="/create-project">
                <CreateProjectRedirection />
              </PrivateRoute>
              <PrivateRoute path="/onboard-user/:inviteCode?">
                <OnboardingUser />
              </PrivateRoute>
              <Route path="/:companyId" component={NestedRoutes} />
              <Route path="/" component={HomeRoute} exact={true} />
            </Switch>
          </QueryParamProvider>
        </Router>
      </Suspense>
    </ErrorBoundary>
  )
}

export default Routes
